import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { getAvailableWidgets } from '../../../../../services/dashboardBuilder';

const VIEW_MODES = {
  TEXT: 'text',
  IMAGE: 'image',
};

const ALL_CATEGORY = 'All';

export const useWidgetLibrary = ({ open, sectionId }) => {
  const requestIdRef = useRef(0);
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState(VIEW_MODES.TEXT);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORY);
  const [selectedWidgets, setSelectedWidgets] = useState(new Set());
  const [widgetSizeConfigs, setWidgetSizeConfigs] = useState({});

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setSelectedCategory(ALL_CATEGORY);
      setSelectedWidgets(new Set());
      setWidgetSizeConfigs({});
      return;
    }

    let isMounted = true;
    const requestId = ++requestIdRef.current;

    const loadWidgets = async () => {
      setLoading(true);
      try {
        const response = await getAvailableWidgets(sectionId);
        if (!isMounted || requestId !== requestIdRef.current) return;
        const body = Array.isArray(response?.body) ? response.body : [];
        setWidgets(body);
      } catch (err) {
        console.error('Error fetching widget library:', err);
        if (!isMounted || requestId !== requestIdRef.current) return;
        setWidgets([]);
      } finally {
        if (!isMounted || requestId !== requestIdRef.current) return;
        setLoading(false);
      }
    };

    loadWidgets();

    return () => {
      isMounted = false;
    };
  }, [open, sectionId]);

  const categories = useMemo(() => {
    const unique = [...new Set(widgets.map((w) => w.category).filter(Boolean))];
    return [ALL_CATEGORY, ...unique.sort()];
  }, [widgets]);

  const filteredWidgets = useMemo(() => {
    let result = widgets;

    if (selectedCategory !== ALL_CATEGORY) {
      result = result.filter((w) => w.category === selectedCategory);
    }

    if (searchTerm.trim()) {
      const keyword = searchTerm.toLowerCase().trim();
      result = result.filter(
        (w) =>
          w.name?.toLowerCase().includes(keyword) ||
          w.description?.toLowerCase().includes(keyword) ||
          w.category?.toLowerCase().includes(keyword)
      );
    }

    return result;
  }, [widgets, selectedCategory, searchTerm]);

  const toggleViewMode = useCallback(() => {
    setViewMode((prev) =>
      prev === VIEW_MODES.TEXT ? VIEW_MODES.IMAGE : VIEW_MODES.TEXT
    );
  }, []);

  const toggleWidget = useCallback((widgetId) => {
    flushSync(() => {
      setSelectedWidgets((prev) => {
        const next = new Set(prev);
        if (next.has(widgetId)) {
          next.delete(widgetId);
        } else {
          next.add(widgetId);
        }
        return next;
      });
    });
    setWidgetSizeConfigs((prev) => {
      if (!prev[widgetId]) return prev;
      const { [widgetId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const addWidgetFromDetail = useCallback((widgetId, size) => {
    flushSync(() => {
      setSelectedWidgets((prev) => new Set(prev).add(widgetId));
    });
    setWidgetSizeConfigs((prev) => ({ ...prev, [widgetId]: size }));
  }, []);

  const selectedWidgetObjects = useMemo(() => {
    return widgets
      .filter((w) => selectedWidgets.has(w.id))
      .map((w) => ({ ...w, selectedSize: widgetSizeConfigs[w.id] || w.defaultSize }));
  }, [widgets, selectedWidgets, widgetSizeConfigs]);

  return {
    widgets: filteredWidgets,
    loading,
    viewMode,
    searchTerm,
    selectedCategory,
    selectedWidgets,
    selectedWidgetObjects,
    categories,

    setSearchTerm,
    setSelectedCategory,
    toggleViewMode,
    toggleWidget,
    addWidgetFromDetail,
  };
};
